import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Group, Line, Circle, Rect, Text } from 'react-konva';
import { LineDotRightHorizontal, Trash2 } from 'lucide-react';
import {
  STAGE_HEIGHT_PX,
  computeDisplayScale,
  computeFitTransform,
  stageToImagePoint,
  visualHandleRadius,
} from '../lib/floorPlanKonvaViewport.js';
import {
  addPoint,
  addDoorObject,
  addFurnitureObject,
  addWindowObject,
  cleanupFloorPlanState,
  clearActiveChain,
  DEFAULT_DOOR,
  DEFAULT_WALL_COLOR,
  DEFAULT_WALL_THICKNESS_M,
  DEFAULT_WINDOW,
  findPointNear,
  getOpeningById,
  getPointById,
  getWallSegmentsResolved,
  insertPointOnWall,
  isWallSegmentLongEnough,
  movePoint,
  removeOpening,
  removePointAndHealWalls,
  removeWall,
  setWallMaterial,
  splitWallAtPoint,
  splitWallsAtPointIfOnSegment,
  updateOpening,
  updateWall,
} from '../lib/floorPlanDesignerState.js';
import { addWallResolvingCrossings } from '../lib/floorPlanDesignerWallCrossings.js';
import {
  buildAllWallRenderData2D,
  getBoundaryWallVisualGeometry,
  getPhysicalWallPolygon,
  getWallPolygonDrawListForWall,
} from '../lib/floorPlanDesignerWallPolygons.js';
import {
  clampToCanvas,
  findClosedPolygons,
  findNearestWall,
  findRoomContainingPoint,
  getVisibleBounds,
  polygonCentroid,
  roomAxisAlignedBoundsM,
  segmentAngleDeg,
  segmentMidpoint,
} from '../lib/floorPlanDesignerGeometry.js';
import { buildRoomBoundaryModel } from '../lib/floorPlanDesignerRoomBoundaries.js';
import {
  ANGLE_SNAP_TOLERANCE_DEG,
  ORTHO_ANGLE_SNAP_DEG,
  resolveOrthogonalAndOTrackSnap,
  resolveWallTranslationPerpendicularSnap,
  resolveWallTranslationOTrackSnap,
  screenToleranceToImagePx,
  snapDraggedCorner,
  snapPointToPerpendicularFromAnchor,
  snapPointer,
} from '../lib/floorPlanDesignerSnap.js';
import {
  buildCenterlineWallDimension,
  buildWallEdgeDimensions,
  estimateTextHalfWidth,
  formatRoomLabel,
} from '../lib/floorPlanDesignerDimensions.js';
import { FIT_PADDING_PX } from '../lib/floorPlanDesignerConfig.js';
import { findNearestWallLineSnap } from '../lib/floorPlanDesignerWallSnap.js';
import {
  addRectangularRoom,
  DEFAULT_ROOM_FLOOR_COLOR,
  deleteRoom,
  getRoomIdForDetectedRoom,
  getRoomMeta,
  isRectangularRoomLargeEnough,
  resolveRectangularRoomCorner,
  resizeRoomAxisAligned,
  setRoomFloorMaterial,
  setRoomName,
  setRoomVisibleIn3D,
  syncRoomsMeta,
} from '../lib/floorPlanDesignerRooms.js';
import {
  applyDesignerDefaultViewport,
  clampDesignerUserZoom,
  computeDesignerScreenConstants,
  designerDebug,
  DESIGNER_FIXED_SCREEN_HIT_PX,
  DESIGNER_ZOOM_STEP,
  MIN_WALL_HIT_WIDTH_PX,
  pointerToWorld,
  worldToOverlay,
} from '../lib/floorPlanDesignerViewport.js';
import {
  DRAWABLE_FRAME_COLOR,
  ROOM_DETECT_DEBUG,
  SHOW_ROOM_BOUNDARY_DEBUG,
} from '../lib/floorPlanDesignerConfig.js';
import { getNodeVisualCenter } from '../lib/floorPlanDesignerNodeDisplay.js';
import FloorPlanRoomActions from './FloorPlanRoomActions.jsx';
import FloorPlanRoomEditPopup from './FloorPlanRoomEditPopup.jsx';
import FloorPlanWallActions from './FloorPlanWallActions.jsx';
import FloorPlanWallEditPopup from './FloorPlanWallEditPopup.jsx';
import FloorPlanDesignerDimension from './FloorPlanDesignerDimension.jsx';
import FloorPlanMaterialPickerPopup from './FloorPlanMaterialPickerPopup.jsx';
import FloorPlanBackgroundImageLayer from './FloorPlanBackgroundImageLayer.jsx';
import { getWallMaterialConfig, getRoomFloorMaterialConfig } from '../lib/floorPlanMaterialConfig.js';
import { updateBackgroundReferenceImage } from '../lib/floorPlanBackgroundImageState.js';
import FloorPlanOpeningLayer from './FloorPlanOpeningLayer.jsx';
import FloorPlanOpeningActions from './FloorPlanOpeningActions.jsx';
import FloorPlanOpeningEditPopup from './FloorPlanOpeningEditPopup.jsx';
import FloorPlanOpeningPlacementPreview from './FloorPlanOpeningPlacementPreview.jsx';
import SelectionActionMenu from '../features/floorPlanEditor/FloorPlanWorkspace/SelectionActionMenu.jsx';
import RectangularRoomPreview from '../features/floorPlanEditor/FloorPlanWorkspace/RectangularRoomPreview.jsx';
import { resolveDimensionPlacement, inflateDimensionTextBounds } from '../features/floorPlanEditor/FloorPlanWorkspace/useDimensionPlacement.js';
import { getDefaultFloorPlanBlockedBounds } from '../features/floorPlanEditor/FloorPlanWorkspace/useOverlayPlacement.js';
import { useFloorPlanHover } from '../hooks/useFloorPlanHover.js';
import { resolveSelectableColor } from '../lib/floorPlanSelectableVisualState.js';
import {
  buildWallMountedOpeningItems,
  OPENING_2D_THICKNESS_M,
  OPENING_COLORS,
  openingPreviewLayout,
  resolveOpeningWallPlacement,
} from '../lib/floorPlanOpening2D.js';

/** @typedef {import('../lib/floorPlanDesignerState.js').DesignerState} DesignerState */
/** @typedef {[number, number]} Point */

const COLORS = {
  wall: '#5f6b73',
  wallHover: '#66D98A',
  wallSelected: '#66D98A',
  preview: '#66D98A',
  door: '#ea580c',
  window: '#06b6d4',
  floor: DEFAULT_ROOM_FLOOR_COLOR,
  point: 'rgba(100, 190, 255, 0.7)',
  pointStroke: 'rgba(50, 145, 220, 0.9)',
  pointHover: 'rgba(100, 190, 255, 0.92)',
  pointSelected: '#66D98A',
  perpendicularMarker: '#ef4444',
};

const WALL_DRAG_THRESHOLD_PX = 4;
const SNAP_GUIDE_RAY_LENGTH_PX = 10000;
const PERPENDICULAR_MARKER_METERS = 0.1;

function unitVectorFromTo(from, to) {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  return [dx / len, dy / len];
}

function buildPerpendicularMarkerPolygon(guide, pixelsPerMeter) {
  const corner = guide.corner;
  const legA = unitVectorFromTo(corner, guide.legAEnd);
  const legB = unitVectorFromTo(corner, guide.legBEnd);
  if (!legA || !legB) return null;
  const size = PERPENDICULAR_MARKER_METERS * pixelsPerMeter;
  const p1 = [corner[0] + legA[0] * size, corner[1] + legA[1] * size];
  const p2 = [p1[0] + legB[0] * size, p1[1] + legB[1] * size];
  const p3 = [corner[0] + legB[0] * size, corner[1] + legB[1] * size];
  return [corner, p1, p2, p3];
}

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}

function projectPointToSegmentRatio(point, start, end) {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-6) return 0.5;
  return clamp01(((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / lenSq);
}

function pointAtSegmentRatio(start, end, ratio) {
  return [
    start[0] + (end[0] - start[0]) * ratio,
    start[1] + (end[1] - start[1]) * ratio,
  ];
}

/**
 * @param {{ bounds: { minX: number; maxX: number; minY: number; maxY: number }; pixelsPerMeter: number; stepM: number; stroke: string; strokeWidth?: number }} props
 */
function MeterGridLines({ bounds, pixelsPerMeter, stepM, stroke, strokeWidth = 1 }) {
  const stepPx = stepM * pixelsPerMeter;
  if (stepPx <= 0) return null;

  const startX = Math.floor(bounds.minX / stepPx) * stepPx;
  const startY = Math.floor(bounds.minY / stepPx) * stepPx;
  const lines = [];

  for (let x = startX; x <= bounds.maxX; x += stepPx) {
    lines.push(
      <Line
        key={`v-${stepM}-${x}`}
        points={[x, bounds.minY, x, bounds.maxY]}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeScaleEnabled={false}
        listening={false}
      />,
    );
  }
  for (let y = startY; y <= bounds.maxY; y += stepPx) {
    lines.push(
      <Line
        key={`h-${stepM}-${y}`}
        points={[bounds.minX, y, bounds.maxX, y]}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeScaleEnabled={false}
        listening={false}
      />,
    );
  }
  return <>{lines}</>;
}

/**
 * @param {{
 *   state: DesignerState;
 *   onStateChange: (next: DesignerState) => void;
 *   historyApi?: {
 *     commit: (next: DesignerState) => void;
 *     apply: (next: DesignerState) => void;
 *     beginTransaction: () => void;
 *     endTransaction: (next: DesignerState) => void;
 *     clearHistory: () => void;
 *   };
 *   activeTool: string;
 *   onActiveToolChange: (tool: string) => void;
 *   openingPreset?: { widthMeters: number; heightMeters: number; sillHeightMeters: number } | null;
 *   snapToGrid: boolean;
 *   snapToPoints: boolean;
 *   snapGridMeters: number;
 *   snapTolerancePx: number;
 *   onRegisterViewportActions?: (actions: {
 *     fit: () => void;
 *     fitBounds?: (bounds: { minX: number; maxX: number; minY: number; maxY: number; widthM: number; heightM: number }, options?: { paddingRatio?: number }) => void;
 *     resetZoom: () => void;
 *     undo?: () => void;
 *     redo?: () => void;
 *     zoomIn?: () => void;
 *     zoomOut?: () => void;
 *     resetInteraction?: () => void;
 *     clearSelection?: () => void;
 *   }) => void;
 *   onRoomSelectionChange?: (selection: object | null) => void;
 *   onRenderRoomRequest?: (selection: object) => void;
 *   enableBackgroundReferenceImage?: boolean;
 *   backgroundEditMode?: boolean;
 *   subcategoryPanelOpen?: boolean;
 * }} props
 */
function FloorPlanDesignerCanvas({
  state,
  onStateChange,
  historyApi,
  activeTool,
  onActiveToolChange,
  openingPreset = null,
  snapToGrid,
  snapToPoints,
  snapGridMeters = 0.1,
  snapTolerancePx,
  onRegisterViewportActions,
  onRoomSelectionChange,
  onRenderRoomRequest,
  enableBackgroundReferenceImage = false,
  backgroundEditMode = false,
  subcategoryPanelOpen = true,
}) {
  const containerRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const [stageWidth, setStageWidth] = useState(800);
  const [stageHeight, setStageHeight] = useState(STAGE_HEIGHT_PX);
  const [userZoom, setUserZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const { hoverTarget, setHover, clearHover, isHovered } = useFloorPlanHover();
  const hoverWallId = hoverTarget?.kind === 'wall' ? hoverTarget.id : null;
  const hoverPointId = hoverTarget?.kind === 'point' ? hoverTarget.id : null;
  const hoverOpeningId = hoverTarget?.kind === 'opening' ? hoverTarget.id : null;
  const [materialPickerTarget, setMaterialPickerTarget] = useState(
    /** @type {{ kind: 'wall' | 'room'; id: string } | null} */ (null),
  );
  const [openingEditOpen, setOpeningEditOpen] = useState(false);
  const [wallEditOpen, setWallEditOpen] = useState(false);
  const [roomEditOpen, setRoomEditOpen] = useState(false);
  const [selection, setSelection] = useState(null);
  const [selectionMenuVisible, setSelectionMenuVisible] = useState(false);
  const [selectionMenuSuppressed, setSelectionMenuSuppressed] = useState(false);
  const [floatingActionsAnchor, setFloatingActionsAnchor] = useState(
    /** @type {{ x: number; y: number } | null} */ (null),
  );
  const [liveDimWallIds, setLiveDimWallIds] = useState([]);
  const [openingPreview, setOpeningPreview] = useState(null);
  const [openingPointerPoint, setOpeningPointerPoint] = useState(/** @type {Point | null} */ (null));
  const [roomDrawAnchor, setRoomDrawAnchor] = useState(/** @type {Point | null} */ (null));
  const [roomDrawPreview, setRoomDrawPreview] = useState(/** @type {Point | null} */ (null));
  const [actionMenuBounds, setActionMenuBounds] = useState(
    /** @type {{ x: number; y: number; width: number; height: number; placement?: string } | null} */ (null),
  );
  const [selectedRoom, setSelectedRoom] = useState(
    /** @type {{ roomId: string; roomIndex: number; wallIds: string[]; boundaryWallIds: string[]; pointIds: string[]; polygon: Point[] } | null} */ (
      null
    ),
  );
  const [snapGuides, setSnapGuides] = useState(
    /** @type {Array<
     *   | { type: 'angle'; anchor: Point; angleDeg: number }
     *   | { type: 'horizontal' | 'vertical'; from: { x: number; y: number }; to: { x: number; y: number }; sourcePointId?: string; reason?: string }
     * >} */ ([]),
  );
  const panDragRef = useRef({ active: false, startX: 0, startY: 0, originPanX: 0, originPanY: 0 });
  const viewportInitializedRef = useRef(false);
  /** @type {React.MutableRefObject<{ mode: 'idle' | 'draggingWall' | 'draggingPoint' | 'draggingOpening' | 'panning'; wallId?: string; openingId?: string; pointerStartWorld?: Point; pointsStart?: Record<string, Point> }>} */
  const interactionRef = useRef({ mode: 'idle' });
  const wallDragPendingRef = useRef(
    /** @type {{ wallId: string; startScreenX: number; startScreenY: number; startWorld: Point } | null} */ (null),
  );
  const ignoreSelectionClickUntilRef = useRef(0);
  const previousRoomIdsRef = useRef(/** @type {string[]} */ ([]));

  const canvasW = state.canvasWidth;
  const canvasH = state.canvasHeight;
  const ppm = state.scale.pixelsPerMeter;
  const mpp = state.scale.metersPerPixel;

  const { fitScale, offsetX, offsetY } = useMemo(
    () => computeFitTransform(stageWidth, stageHeight, canvasW, canvasH),
    [stageWidth, stageHeight, canvasW, canvasH],
  );
  const zoomClampCtx = useMemo(
    () => ({ stageWidth, pixelsPerMeter: ppm, fitScale }),
    [stageWidth, ppm, fitScale],
  );
  const displayScale = useMemo(() => computeDisplayScale(fitScale, userZoom), [fitScale, userZoom]);
  const screenConstants = useMemo(
    () => computeDesignerScreenConstants(displayScale),
    [displayScale],
  );
  const viewportTransform = useMemo(
    () => ({ offsetX, offsetY, displayScale, panX, panY }),
    [offsetX, offsetY, displayScale, panX, panY],
  );

  const segments = useMemo(() => getWallSegmentsResolved(state), [state]);
  const closedRooms = useMemo(() => findClosedPolygons(state), [state]);
  const roomPolygons = useMemo(
    () => closedRooms.map((room) => room.polygon),
    [closedRooms],
  );
  const roomBoundaryModel = useMemo(() => buildRoomBoundaryModel(state), [state]);
  const detectedRooms = roomBoundaryModel.rooms;
  const wallRenderData = useMemo(() => buildAllWallRenderData2D(state, ppm), [state, ppm]);
  const wallPolygons = wallRenderData.polygons;
  const wallRenderModel = wallRenderData.renderModel;

  useEffect(() => {
    designerDebug('current walls', state.walls.length);
    designerDebug('current rooms', detectedRooms.length);
  }, [state.walls.length, detectedRooms.length]);

  const closedRoomIds = useMemo(
    () => closedRooms.map((room) => getRoomIdForDetectedRoom(room)),
    [closedRooms],
  );

  useEffect(() => {
    if (!ROOM_DETECT_DEBUG) return;
    const prev = previousRoomIdsRef.current;
    const next = closedRoomIds;
    const changed =
      prev.length !== next.length || prev.some((id, i) => id !== next[i]);
    if (changed) {
      console.debug('[Room Detection] rooms changed', {
        previousRoomIds: prev,
        nextRoomIds: next,
        pointsCount: state.points.length,
        wallsCount: state.walls.length,
        draggingWallId:
          interactionRef.current.mode === 'draggingWall'
            ? interactionRef.current.wallId
            : null,
      });
    }
    previousRoomIdsRef.current = next;
  }, [closedRoomIds, state.points.length, state.walls.length]);

  const syncSelectionAfterClean = useCallback((cleaned) => {
    setSelection((prev) => {
      if (!prev) return null;
      if (prev.type === 'wall' && !cleaned.walls.some((w) => w.id === prev.id)) return null;
      if (prev.type === 'point' && !cleaned.points.some((p) => p.id === prev.id)) return null;
      if (prev.type === 'opening' && !cleaned.openings.some((opening) => opening.id === prev.id)) {
        return null;
      }
      if (prev.type === 'object' && !(cleaned.objects || []).some((object) => object.id === prev.id)) {
        return null;
      }
      return prev;
    });
    setSelectedRoom((prev) => {
      if (!prev) return null;
      const model = buildRoomBoundaryModel(cleaned);
      const match = model.rooms.find((room) => room.id === prev.roomId);
      if (!match) return null;
      return {
        ...prev,
        wallIds: match.wallIds,
        boundaryWallIds: match.boundaryWallIds,
        pointIds: match.pointIds,
        polygon: match.polygon,
      };
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelection(null);
    setSelectedRoom(null);
    setFloatingActionsAnchor(null);
    setSelectionMenuVisible(false);
    setSelectionMenuSuppressed(false);
    setMaterialPickerTarget(null);
    setOpeningEditOpen(false);
    setSnapGuides([]);
    onRoomSelectionChange?.(null);
  }, [onRoomSelectionChange]);

  const revealSelectionMenu = useCallback((anchor = undefined) => {
    setSelectionMenuSuppressed(false);
    setSelectionMenuVisible(true);
    if (anchor !== undefined) {
      setFloatingActionsAnchor(anchor);
    }
  }, []);

  const suppressSelectionMenuForDrag = useCallback(() => {
    setSelectionMenuVisible(false);
    setSelectionMenuSuppressed(true);
    setFloatingActionsAnchor(null);
  }, []);

  const markDragCompleted = useCallback(() => {
    setSelectionMenuVisible(false);
    ignoreSelectionClickUntilRef.current = performance.now() + 120;
  }, []);

  const shouldIgnoreSelectionClickAfterDrag = useCallback(() => {
    if (performance.now() <= ignoreSelectionClickUntilRef.current) {
      return true;
    }
    ignoreSelectionClickUntilRef.current = 0;
    return false;
  }, []);

  const pushHistory = useCallback(
    (next, options = {}) => {
      const cleaned = cleanupFloorPlanState(next, {
        keepChainPoints: Boolean(options.keepChainPoints),
      });
      const synced = syncRoomsMeta(cleaned, findClosedPolygons(cleaned));
      stateRef.current = synced;
      if (historyApi) {
        historyApi.commit(synced);
      } else {
        onStateChange(synced);
      }
      syncSelectionAfterClean(synced);
    },
    [historyApi, onStateChange, syncSelectionAfterClean],
  );

  const applyState = useCallback(
    (next) => {
      stateRef.current = next;
      onStateChange(next);
    },
    [onStateChange],
  );

  const resetInteractionState = useCallback(() => {
    designerDebug('interaction reset');
    clearSelection();
    clearHover();
    setOpeningPreview(null);
    setOpeningPointerPoint(null);
    setRoomDrawAnchor(null);
    setRoomDrawPreview(null);
    setLiveDimWallIds([]);
    panDragRef.current.active = false;
    interactionRef.current = { mode: 'idle' };
    historyApi?.clearHistory();
  }, [clearSelection, clearHover, historyApi]);

  const captureOverlayAnchorFromEvent = useCallback((e) => {
    const wrap = containerRef.current;
    if (!wrap) return null;
    const wrapRect = wrap.getBoundingClientRect();
    return {
      x: e.evt.clientX - wrapRect.left,
      y: e.evt.clientY - wrapRect.top,
      clientX: e.evt.clientX,
      clientY: e.evt.clientY,
    };
  }, []);

  const selectWall = useCallback((wallId, anchor = undefined, options = {}) => {
    designerDebug('wall selected', wallId);
    setSelectedRoom(null);
    setMaterialPickerTarget(null);
    setOpeningEditOpen(false);
    setSelection({ type: 'wall', id: wallId });
    if (options.suppressMenu) {
      suppressSelectionMenuForDrag();
    } else {
      revealSelectionMenu(anchor);
    }
  }, [revealSelectionMenu, suppressSelectionMenuForDrag]);

  const selectRoomAtEvent = useCallback(
    (e, room, roomIndex) => {
      const anchor = captureOverlayAnchorFromEvent(e);
      setSelection(null);
      setMaterialPickerTarget(null);
      setOpeningEditOpen(false);
      setSnapGuides([]);
      revealSelectionMenu(anchor);
      const detected =
        detectedRooms.find((item) => item.id === getRoomIdForDetectedRoom(room)) ??
        detectedRooms[roomIndex] ??
        null;
      setSelectedRoom({
        roomId: getRoomIdForDetectedRoom(room),
        roomIndex,
        wallIds: room.wallIds,
        boundaryWallIds: detected?.boundaryWallIds ?? [],
        pointIds: room.pointIds,
        polygon: room.polygon,
      });
    },
    [captureOverlayAnchorFromEvent, detectedRooms, revealSelectionMenu],
  );

  const selectPoint = useCallback((pointId, anchor = undefined, options = {}) => {
    setSelectedRoom(null);
    setMaterialPickerTarget(null);
    setOpeningEditOpen(false);
    setSelection({ type: 'point', id: pointId });
    if (options.suppressMenu) {
      suppressSelectionMenuForDrag();
    } else {
      revealSelectionMenu(anchor);
    }
  }, [revealSelectionMenu, suppressSelectionMenuForDrag]);

  const selectOpening = useCallback((openingId, anchor = undefined, options = {}) => {
    setSelectedRoom(null);
    setMaterialPickerTarget(null);
    setOpeningEditOpen(false);
    setSelection({ type: 'opening', id: openingId });
    if (options.suppressMenu) {
      suppressSelectionMenuForDrag();
    } else {
      revealSelectionMenu(anchor);
    }
  }, [revealSelectionMenu, suppressSelectionMenuForDrag]);

  const selectWallAtEvent = useCallback(
    (wallId, e) => {
      const anchor = captureOverlayAnchorFromEvent(e);
      selectWall(wallId, anchor);
    },
    [captureOverlayAnchorFromEvent, selectWall],
  );

  const handleWallSelect = useCallback(
    (e, wallId) => {
      if (shouldIgnoreSelectionClickAfterDrag()) return;
      e.evt.preventDefault();
      e.cancelBubble = true;
      selectWallAtEvent(wallId, e);
    },
    [selectWallAtEvent, shouldIgnoreSelectionClickAfterDrag],
  );

  const undo = useCallback(() => {
    historyApi?.undo?.();
  }, [historyApi]);

  const applyZoomStep = useCallback(
    (direction) => {
      const center = { x: stageWidth / 2, y: stageHeight / 2 };
      const nextZoom = clampDesignerUserZoom(
        userZoom * (direction > 0 ? DESIGNER_ZOOM_STEP : 1 / DESIGNER_ZOOM_STEP),
        zoomClampCtx,
      );
      const imageBefore = stageToImagePoint(center, {
        ...viewportTransform,
        displayScale: computeDisplayScale(fitScale, userZoom),
      });
      setUserZoom(nextZoom);
      const nextDisplay = computeDisplayScale(fitScale, nextZoom);
      setPanX(center.x - offsetX - imageBefore[0] * nextDisplay);
      setPanY(center.y - offsetY - imageBefore[1] * nextDisplay);
    },
    [stageWidth, stageHeight, userZoom, zoomClampCtx, viewportTransform, fitScale, offsetX, offsetY],
  );

  const applyDefaultViewport = useCallback(() => {
    const view = applyDesignerDefaultViewport({
      stageWidth,
      stageHeight,
      canvasW,
      canvasH,
      pixelsPerMeter: ppm,
      fitScale,
      offsetX,
      offsetY,
    });
    setUserZoom(view.userZoom);
    setPanX(view.panX);
    setPanY(view.panY);
  }, [stageWidth, stageHeight, canvasW, canvasH, ppm, fitScale, offsetX, offsetY]);

  const handleFitToView = useCallback(() => {
    applyDefaultViewport();
  }, [applyDefaultViewport]);

  const handleFitToBounds = useCallback(
    (bounds, options = {}) => {
      if (!bounds || stageWidth <= 0) {
        applyDefaultViewport();
        return;
      }

      const width = Math.max(0.1, bounds.maxX - bounds.minX || bounds.widthM || 0);
      const height = Math.max(0.1, bounds.maxY - bounds.minY || bounds.heightM || 0);
      const paddingRatio = options.paddingRatio ?? 0.15;
      const paddingPx = options.paddingPx ?? FIT_PADDING_PX;
      const paddedWidth = width + Math.max(width * paddingRatio * 2, paddingPx * 2);
      const paddedHeight = height + Math.max(height * paddingRatio * 2, paddingPx * 2);
      const chromeLeft = subcategoryPanelOpen ? 64 + 12 + 280 + 12 + 12 : 64 + 12 + 12 + 12;
      const chromeTop = 12 + 52;
      const chromeRight = 140;
      const availableW = Math.max(1, stageWidth - chromeLeft - chromeRight);
      const availableH = Math.max(1, stageHeight - chromeTop - 72);
      const targetDisplayScale = Math.min(availableW / paddedWidth, availableH / paddedHeight);
      const nextUserZoom = clampDesignerUserZoom(targetDisplayScale / fitScale, zoomClampCtx);
      const nextDisplayScale = computeDisplayScale(fitScale, nextUserZoom);
      const centerX = bounds.minX + width / 2;
      const centerY = bounds.minY + height / 2;

      setUserZoom(nextUserZoom);
      setPanX(stageWidth / 2 - offsetX - centerX * nextDisplayScale);
      setPanY(stageHeight / 2 - offsetY - centerY * nextDisplayScale);
    },
    [applyDefaultViewport, fitScale, offsetX, offsetY, stageWidth, stageHeight, zoomClampCtx, subcategoryPanelOpen],
  );

  const handleResetZoom = useCallback(() => {
    applyDefaultViewport();
  }, [applyDefaultViewport]);

  useEffect(() => {
    onRegisterViewportActions?.({
      fit: handleFitToView,
      fitBounds: handleFitToBounds,
      resetZoom: handleResetZoom,
      undo,
      zoomIn: () => applyZoomStep(1),
      zoomOut: () => applyZoomStep(-1),
      resetInteraction: resetInteractionState,
      clearSelection,
    });
  }, [
    onRegisterViewportActions,
    handleFitToView,
    handleFitToBounds,
    handleResetZoom,
    undo,
    applyZoomStep,
    resetInteractionState,
    clearSelection,
  ]);

  useEffect(() => {
    if (state.walls.length === 0 && state.points.length === 0) {
      clearSelection();
      clearHover();
      setOpeningPreview(null);
      setOpeningPointerPoint(null);
      setRoomDrawAnchor(null);
      setRoomDrawPreview(null);
      setLiveDimWallIds([]);
      panDragRef.current.active = false;
      interactionRef.current = { mode: 'idle' };
      historyApi?.clearHistory();
    }
  }, [state.walls.length, state.points.length, historyApi?.clearHistory, clearSelection, clearHover]);

  useEffect(() => {
    designerDebug('active tool changed', activeTool);
    if (activeTool !== 'wall') {
      setSnapGuides([]);
    }
    if (activeTool !== 'room') {
      setRoomDrawAnchor(null);
      setRoomDrawPreview(null);
    }
  }, [activeTool]);

  useEffect(() => {
    const current = stateRef.current;
    if (!closedRooms.length) return;
    const needsSync = closedRooms.some(
      (room) => !current.roomsMeta?.[getRoomIdForDetectedRoom(room)],
    );
    if (!needsSync) return;
    const synced = syncRoomsMeta(current, closedRooms);
    stateRef.current = synced;
    onStateChange(synced);
  }, [closedRooms, onStateChange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setStageWidth(Math.max(320, Math.round(rect.width)));
      setStageHeight(Math.max(420, Math.round(rect.height || STAGE_HEIGHT_PX)));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (viewportInitializedRef.current || stageWidth <= 0 || stageHeight <= 0) return;
    viewportInitializedRef.current = true;
    applyDefaultViewport();
  }, [stageWidth, stageHeight, applyDefaultViewport]);

  const applySnapGuidesFromResult = useCallback((snapResult) => {
    const guides = [];
    if (
      !snapResult?.perpendicularGuide &&
      snapResult?.angleSnap?.snapped &&
      snapResult.angleSnap.anchor &&
      snapResult.angleSnap.snappedAngleDeg != null
    ) {
      guides.push({
        type: 'angle',
        anchor: snapResult.angleSnap.anchor,
        angleDeg: snapResult.angleSnap.snappedAngleDeg,
      });
    }
    if (snapResult?.guides?.length) {
      guides.push(...snapResult.guides);
    }
    if (snapResult?.perpendicularGuide) {
      guides.push(snapResult.perpendicularGuide);
    }
    setSnapGuides(guides);
  }, []);

  const pointerToImage = useCallback(
    (stage) => {
      const pointer = stage.getPointerPosition();
      if (!pointer) return null;
      return clampToCanvas(pointerToWorld(pointer, viewportTransform), canvasW, canvasH);
    },
    [viewportTransform, canvasW, canvasH],
  );

  const getChainExcludeWallIds = useCallback((current) => {
    const chainPointSet = new Set(current.activeChainPointIds);
    return current.walls
      .filter((w) => chainPointSet.has(w.startPointId) && chainPointSet.has(w.endPointId))
      .map((w) => w.id);
  }, []);

  const resolveDrawSnap = useCallback(
    (pointer, opts = {}) => {
      const current = stateRef.current;
      if (opts.altKey) {
        return { point: pointer, angleSnap: null, wallSnap: null, existingPointId: null, guides: [] };
      }

      const tolerance = snapTolerancePx / displayScale;

      if (snapToPoints) {
        const near = findPointNear(current, pointer, tolerance);
        if (near) {
          return {
            point: [near.x, near.y],
            angleSnap: null,
            wallSnap: null,
            existingPointId: near.id,
            guides: [],
          };
        }
      }

      const wallSnap = findNearestWallLineSnap(pointer, current, {
        snapTolerancePx,
        displayScale,
        excludeWallIds: getChainExcludeWallIds(current),
      });
      if (wallSnap.snapped && wallSnap.point) {
        return {
          point: wallSnap.point,
          angleSnap: null,
          wallSnap,
          existingPointId: null,
          guides: [],
        };
      }

      const chain = current.activeChainPointIds;
      const lastId = chain[chain.length - 1];
      const lastPt = lastId ? getPointById(current, lastId) : null;
      const anchor = lastPt ? [lastPt.x, lastPt.y] : null;
      const axisSnap = resolveOrthogonalAndOTrackSnap({
        activePoint: pointer,
        state: current,
        orthoAnchorPointIds: lastId ? [lastId] : [],
        excludePointIds: lastId ? [lastId] : [],
        snapToGrid,
        gridMeters: snapGridMeters,
        displayScale,
        includeConnectedWallOrtho: Boolean(lastId),
        includeOTrack: snapToPoints,
      });
      let point = axisSnap.point;
      let angleSnap = null;
      let perpendicularGuide = null;
      if (lastId) {
        const perpendicularSnap = snapPointToPerpendicularFromAnchor(point, lastId, current, {
          tolerancePx: screenToleranceToImagePx(snapTolerancePx, displayScale),
          angleToleranceDeg: ORTHO_ANGLE_SNAP_DEG,
          excludeWallIds: getChainExcludeWallIds(current),
        });
        if (perpendicularSnap.snapped) {
          point = perpendicularSnap.point;
          angleSnap = perpendicularSnap.angleSnap;
          perpendicularGuide = perpendicularSnap.perpendicularGuide;
        }
      }
      if (!perpendicularGuide && !axisSnap.snapInfo.snappedX && !axisSnap.snapInfo.snappedY) {
        const snapResult = snapPointer(point, current, {
          snapToGrid: false,
          snapToPoints: false,
          gridMeters: snapGridMeters,
          snapTolerancePx,
          displayScale,
          anchor,
          angleSnapEnabled: true,
          angleSnapToleranceDeg: ANGLE_SNAP_TOLERANCE_DEG,
          disableSnap: false,
        });
        point = snapResult.point;
        angleSnap = angleSnap || snapResult.angleSnap;
      }

      return {
        point,
        angleSnap,
        perpendicularGuide,
        wallSnap: null,
        existingPointId: null,
        guides: axisSnap.guides,
      };
    },
    [snapToGrid, snapToPoints, snapGridMeters, snapTolerancePx, displayScale, getChainExcludeWallIds],
  );

  const resolveWallAxisLock = useCallback((startPoint, endPoint) => {
    const dx = endPoint[0] - startPoint[0];
    const dy = endPoint[1] - startPoint[1];
    const length = Math.hypot(dx, dy);
    if (length <= 0.0001) return { start: startPoint, end: endPoint };
    const angleDeg = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
    const axisAngles = [0, 90, 180, 270];
    let bestAngle = null;
    let bestDiff = Infinity;
    for (const target of axisAngles) {
      const diffRaw = Math.abs(angleDeg - target);
      const diff = Math.min(diffRaw, 360 - diffRaw);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestAngle = target;
      }
    }
    if (bestAngle == null || bestDiff > ORTHO_ANGLE_SNAP_DEG) {
      return { start: startPoint, end: endPoint };
    }
    const mid = [(startPoint[0] + endPoint[0]) / 2, (startPoint[1] + endPoint[1]) / 2];
    const rad = (bestAngle * Math.PI) / 180;
    const half = length / 2;
    const ux = Math.cos(rad);
    const uy = Math.sin(rad);
    return {
      start: [mid[0] - ux * half, mid[1] - uy * half],
      end: [mid[0] + ux * half, mid[1] + uy * half],
    };
  }, []);

  const applyWallDragAtPointer = useCallback(
    (worldPointer, wallId) => {
      const drag = interactionRef.current;
      if (drag.mode !== 'draggingWall' || !drag.pointerStartWorld || !drag.pointsStart) return;

      const totalDx = worldPointer[0] - drag.pointerStartWorld[0];
      const totalDy = worldPointer[1] - drag.pointerStartWorld[1];
      const current = stateRef.current;
      const wall = current.walls.find((w) => w.id === wallId);
      if (!wall) return;
      const startOrig = drag.pointsStart[wall.startPointId];
      const endOrig = drag.pointsStart[wall.endPointId];
      if (!startOrig || !endOrig) return;

      const rawTranslatedStart = /** @type {Point} */ ([startOrig[0] + totalDx, startOrig[1] + totalDy]);
      const rawTranslatedEnd = /** @type {Point} */ ([endOrig[0] + totalDx, endOrig[1] + totalDy]);
      const axisLocked = resolveWallAxisLock(rawTranslatedStart, rawTranslatedEnd);
      const draggedPointIds = Object.keys(drag.pointsStart);
      const startAxisSnap = resolveOrthogonalAndOTrackSnap({
        activePoint: axisLocked.start,
        state: current,
        pointId: wall.startPointId,
        excludePointIds: draggedPointIds,
        excludeWallIds: [wallId],
        snapToGrid: false,
        displayScale,
        includeConnectedWallOrtho: true,
        includeOTrack: false,
      });
      const endAxisSnap = resolveOrthogonalAndOTrackSnap({
        activePoint: axisLocked.end,
        state: current,
        pointId: wall.endPointId,
        excludePointIds: draggedPointIds,
        excludeWallIds: [wallId],
        snapToGrid: false,
        displayScale,
        includeConnectedWallOrtho: true,
        includeOTrack: false,
      });
      const translationSnap = resolveWallTranslationOTrackSnap({
        startPoint: startAxisSnap.point,
        endPoint: endAxisSnap.point,
        state: current,
        startPointId: wall.startPointId,
        endPointId: wall.endPointId,
        excludePointIds: draggedPointIds,
        excludeWallIds: [wallId],
        displayScale,
        includeConnectedWallOrtho: false,
      });
      const translatedStart = /** @type {Point} */ ([
        startAxisSnap.point[0] + translationSnap.delta[0],
        startAxisSnap.point[1] + translationSnap.delta[1],
      ]);
      const translatedEnd = /** @type {Point} */ ([
        endAxisSnap.point[0] + translationSnap.delta[0],
        endAxisSnap.point[1] + translationSnap.delta[1],
      ]);
      const perpendicularSnap = resolveWallTranslationPerpendicularSnap({
        startPoint: translatedStart,
        endPoint: translatedEnd,
        state: current,
        startPointId: wall.startPointId,
        endPointId: wall.endPointId,
        excludeWallIds: [wallId],
        displayScale,
        snapTolerancePx,
        angleToleranceDeg: ORTHO_ANGLE_SNAP_DEG,
      });
      const finalStart = /** @type {Point} */ ([
        translatedStart[0] + perpendicularSnap.delta[0],
        translatedStart[1] + perpendicularSnap.delta[1],
      ]);
      const finalEnd = /** @type {Point} */ ([
        translatedEnd[0] + perpendicularSnap.delta[0],
        translatedEnd[1] + perpendicularSnap.delta[1],
      ]);

      let next = current;
      next = movePoint(next, wall.startPointId, finalStart);
      next = movePoint(next, wall.endPointId, finalEnd);
      stateRef.current = next;
      applyState(next);
      setSnapGuides([
        ...startAxisSnap.guides,
        ...endAxisSnap.guides,
        ...translationSnap.guides,
        ...(perpendicularSnap.perpendicularGuide ? [perpendicularSnap.perpendicularGuide] : []),
      ]);

      if (ROOM_DETECT_DEBUG) {
        const nextRooms = findClosedPolygons(next).map((room) => getRoomIdForDetectedRoom(room));
        const prev = previousRoomIdsRef.current;
        if (
          prev.length !== nextRooms.length ||
          prev.some((id, i) => id !== nextRooms[i])
        ) {
          console.debug('[Room Detection] rooms changed', {
            previousRoomIds: prev,
            nextRoomIds: nextRooms,
            pointsCount: next.points.length,
            wallsCount: next.walls.length,
            selectedWallId: wallId,
            draggingWallId: wallId,
          });
        }
      }

      setLiveDimWallIds(
        next.walls
          .filter(
            (w) =>
              w.id === wallId ||
              w.startPointId === wall.startPointId ||
              w.endPointId === wall.endPointId ||
              w.startPointId === wall.endPointId ||
              w.endPointId === wall.startPointId,
          )
          .map((w) => w.id),
      );
    },
    [displayScale, applyState, resolveWallAxisLock, snapTolerancePx],
  );

  const startWallDrag = useCallback(
    (wallId, worldPointer) => {
      const current = stateRef.current;
      const wall = current.walls.find((w) => w.id === wallId);
      if (!wall) return;

      const startPt = getPointById(current, wall.startPointId);
      const endPt = getPointById(current, wall.endPointId);
      if (!startPt || !endPt) return;

      historyApi?.beginTransaction();
      interactionRef.current = {
        mode: 'draggingWall',
        wallId,
        pointerStartWorld: worldPointer,
        pointsStart: {
          [wall.startPointId]: [startPt.x, startPt.y],
          [wall.endPointId]: [endPt.x, endPt.y],
        },
      };
      designerDebug('wall drag start', wallId, worldPointer);
      selectWall(wallId, undefined, { suppressMenu: true });
      setLiveDimWallIds([wallId]);
    },
    [selectWall],
  );

  const exitWallDraw = useCallback(() => {
    const cleaned = cleanupFloorPlanState(clearActiveChain(stateRef.current));
    stateRef.current = cleaned;
    onStateChange(cleaned);
    syncSelectionAfterClean(cleaned);
    setSnapGuides([]);
    onActiveToolChange('select');
  }, [onStateChange, onActiveToolChange, syncSelectionAfterClean]);

  const commitWallPoint = useCallback(
    (rawPointer, evt) => {
      const current = stateRef.current;
      const snapResult = resolveDrawSnap(rawPointer, { altKey: evt?.altKey });
      const snapped = snapResult.point;
      const chain = current.activeChainPointIds;
      const lastId = chain[chain.length - 1];

      if (lastId) {
        const lastPt = getPointById(current, lastId);
        if (lastPt && Math.hypot(lastPt.x - snapped[0], lastPt.y - snapped[1]) < 4) {
          exitWallDraw();
          return;
        }
      }

      let next = current;
      let pointId = snapResult.existingPointId;

      if (
        !pointId &&
        snapResult.wallSnap?.snapped &&
        snapResult.wallSnap.wallId &&
        snapResult.wallSnap.point
      ) {
        const split = splitWallAtPoint(
          current,
          snapResult.wallSnap.wallId,
          snapResult.wallSnap.point,
        );
        next = split.state;
        pointId = split.pointId;
      } else if (!pointId) {
        const tolerance = snapTolerancePx / displayScale;
        const existing = snapToPoints ? findPointNear(next, snapped, tolerance) : null;
        if (existing) {
          pointId = existing.id;
        } else {
          const added = addPoint(next, snapped);
          next = added.state;
          pointId = added.pointId;
        }
      }

      if (pointId) {
        const pointSplit = splitWallsAtPointIfOnSegment(next, pointId, {
          excludeWallIds: getChainExcludeWallIds(next),
        });
        next = pointSplit.state;
      }

      if (lastId && pointId) {
        if (isWallSegmentLongEnough(next, lastId, pointId)) {
          next = addWallResolvingCrossings(next, lastId, pointId);
          const closesLoop =
            chain.length >= 2 && chain.includes(pointId) && pointId !== lastId;
          if (closesLoop) {
            next = clearActiveChain(next);
            pushHistory(next);
            setSnapGuides([]);
            onActiveToolChange('select');
            return;
          }
          next = { ...next, activeChainPointIds: [...chain, pointId], draftPreviewPoint: snapped };
        } else if (!snapResult.existingPointId && !snapResult.wallSnap?.snapped) {
          next = {
            ...next,
            points: next.points.filter((p) => p.id !== pointId),
            activeChainPointIds: chain,
            draftPreviewPoint: snapped,
          };
        } else {
          next = { ...next, activeChainPointIds: chain, draftPreviewPoint: snapped };
        }
      } else {
        next = { ...next, activeChainPointIds: [pointId], draftPreviewPoint: snapped };
      }

      pushHistory(next, { keepChainPoints: true });
    },
    [
      resolveDrawSnap,
      snapToPoints,
      snapTolerancePx,
      displayScale,
      pushHistory,
      exitWallDraw,
      getChainExcludeWallIds,
      onActiveToolChange,
    ],
  );

  useEffect(() => {
    if (activeTool === 'room') return;
    setRoomDrawAnchor(null);
    setRoomDrawPreview(null);
  }, [activeTool]);

  useEffect(() => {
    setActionMenuBounds(null);
  }, [selection]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        const current = stateRef.current;
        if (current.activeChainPointIds.length) {
          const cleaned = cleanupFloorPlanState(clearActiveChain(current));
          stateRef.current = cleaned;
          applyState(cleaned);
          syncSelectionAfterClean(cleaned);
        }
        onActiveToolChange('select');
        interactionRef.current = { mode: 'idle' };
        panDragRef.current.active = false;
        setOpeningPreview(null);
        setOpeningPointerPoint(null);
        setRoomDrawAnchor(null);
        setRoomDrawPreview(null);
        clearSelection();
        setLiveDimWallIds([]);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [applyState, onActiveToolChange, syncSelectionAfterClean, clearSelection]);

  const handleWheel = useCallback(
    (e) => {
      e.evt.preventDefault();
      const stage = e.target.getStage();
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      const direction = e.evt.deltaY > 0 ? -1 : 1;
      const nextZoom = clampDesignerUserZoom(
        userZoom * (direction > 0 ? DESIGNER_ZOOM_STEP : 1 / DESIGNER_ZOOM_STEP),
        zoomClampCtx,
      );
      const imageBefore = stageToImagePoint(pointer, {
        ...viewportTransform,
        displayScale: computeDisplayScale(fitScale, userZoom),
      });
      setUserZoom(nextZoom);
      const nextDisplay = computeDisplayScale(fitScale, nextZoom);
      setPanX(pointer.x - offsetX - imageBefore[0] * nextDisplay);
      setPanY(pointer.y - offsetY - imageBefore[1] * nextDisplay);
    },
    [userZoom, fitScale, offsetX, offsetY, viewportTransform, zoomClampCtx],
  );

  const handleContextMenu = useCallback(
    (e) => {
      e.evt.preventDefault();
      const stage = e.target.getStage();
      const pt = pointerToImage(stage);
      if (!pt) return;

      if (activeTool === 'room' && roomDrawAnchor) {
        setRoomDrawAnchor(null);
        setRoomDrawPreview(null);
        setSnapGuides([]);
        return;
      }

      if (activeTool === 'wall' && stateRef.current.activeChainPointIds.length) {
        exitWallDraw();
        return;
      }

      const current = stateRef.current;
      const nearWall = findNearestWall(current, pt, snapTolerancePx / displayScale + 8);
      if (nearWall) {
        if (shouldIgnoreSelectionClickAfterDrag()) return;
        selectWallAtEvent(nearWall.wallId, e);
        return;
      }

      const nearPoint = current.points.find(
        (p) => Math.hypot(p.x - pt[0], p.y - pt[1]) <= snapTolerancePx / displayScale + 6,
      );
      if (nearPoint) {
        if (shouldIgnoreSelectionClickAfterDrag()) return;
        selectPoint(nearPoint.id, captureOverlayAnchorFromEvent(e));
        return;
      }

      clearSelection();
      setOpeningPreview(null);
      setOpeningPointerPoint(null);
      if (activeTool !== 'select') onActiveToolChange('select');
    },
    [
      pointerToImage,
      activeTool,
      roomDrawAnchor,
      exitWallDraw,
      snapTolerancePx,
      displayScale,
      onActiveToolChange,
      selectWallAtEvent,
      selectPoint,
      clearSelection,
      shouldIgnoreSelectionClickAfterDrag,
    ],
  );

  const handleStageClick = useCallback(
    (e) => {
      if (e.evt.button !== 0) return;
      if (interactionRef.current.mode !== 'idle') return;
      const stage = e.target.getStage();
      const pt = pointerToImage(stage);
      if (!pt) return;

      if (activeTool === 'wall') {
        commitWallPoint(pt, e.evt);
        return;
      }

      if (activeTool === 'door' || activeTool === 'window') {
        const nearest = findNearestWall(stateRef.current, pt, snapTolerancePx / displayScale + 12);
        if (nearest) {
          const openingOverrides = openingPreset
            ? {
                widthMeters: openingPreset.widthMeters,
                heightMeters: openingPreset.heightMeters,
                sillHeightMeters: openingPreset.sillHeightMeters,
              }
            : undefined;
          const next =
            activeTool === 'door'
              ? addDoorObject(stateRef.current, nearest.wallId, nearest.centerRatio, openingOverrides)
              : addWindowObject(stateRef.current, nearest.wallId, nearest.centerRatio, openingOverrides);
          pushHistory(next);
          setOpeningPreview(null);
          setOpeningPointerPoint(null);
          onActiveToolChange('select');
        }
        return;
      }

      if (activeTool === 'room') {
        return;
      }

      if (activeTool.startsWith('furniture:')) {
        const category = activeTool.split(':')[1];
        const next = addFurnitureObject(stateRef.current, category, {
          x: pt[0] * mpp,
          z: pt[1] * mpp,
        });
        pushHistory(next);
        onActiveToolChange('select');
        return;
      }

      if (activeTool === 'delete') {
        const nearest = findNearestWall(stateRef.current, pt, snapTolerancePx / displayScale + 12);
        if (nearest) {
          pushHistory(removeWall(stateRef.current, nearest.wallId));
          clearSelection();
        }
        return;
      }

      if (activeTool === 'select') {
        const stage = e.target.getStage();
        const hitRadius = visualHandleRadius(displayScale) + 4 / displayScale;
        const nearPoint = stateRef.current.points.find(
          (p) => Math.hypot(p.x - pt[0], p.y - pt[1]) <= hitRadius,
        );
        if (nearPoint) {
          if (e.target !== stage) return;
          if (shouldIgnoreSelectionClickAfterDrag()) return;
          selectPoint(nearPoint.id, captureOverlayAnchorFromEvent(e));
          return;
        }

        const nearWall = findNearestWall(
          stateRef.current,
          pt,
          snapTolerancePx / displayScale + 8,
        );
        if (nearWall) {
          if (e.target !== stage) return;
          if (shouldIgnoreSelectionClickAfterDrag()) return;
          handleWallSelect(e, nearWall.wallId);
          return;
        }

        if (e.target !== stage) return;

        const roomHit = findRoomContainingPoint(
          detectedRooms.length ? detectedRooms : closedRooms,
          pt,
        );
        if (roomHit) {
          if (shouldIgnoreSelectionClickAfterDrag()) return;
          selectRoomAtEvent(e, roomHit.room, roomHit.roomIndex);
          return;
        }

        clearSelection();
      }
    },
    [
      pointerToImage,
      activeTool,
      commitWallPoint,
      snapTolerancePx,
      displayScale,
      mpp,
      pushHistory,
      onActiveToolChange,
      openingPreset,
      clearSelection,
      handleWallSelect,
      selectPoint,
      selectRoomAtEvent,
      shouldIgnoreSelectionClickAfterDrag,
      closedRooms,
      detectedRooms,
    ],
  );

  const handleMouseMove = useCallback(
    (e) => {
      const stage = e.target.getStage();
      const pt = pointerToImage(stage);
      if (!pt) return;

      const current = stateRef.current;

      const pendingWallDrag = wallDragPendingRef.current;
      if (pendingWallDrag && interactionRef.current.mode === 'idle') {
        const pointer = stage.getPointerPosition();
        if (pointer) {
          const moved = Math.hypot(
            pointer.x - pendingWallDrag.startScreenX,
            pointer.y - pendingWallDrag.startScreenY,
          );
          if (moved > WALL_DRAG_THRESHOLD_PX) {
            wallDragPendingRef.current = null;
            startWallDrag(pendingWallDrag.wallId, pendingWallDrag.startWorld);
          }
        }
      }

      if (interactionRef.current.mode === 'draggingWall' && interactionRef.current.wallId) {
        applyWallDragAtPointer(pt, interactionRef.current.wallId);
        return;
      }

      if (activeTool === 'wall' && interactionRef.current.mode === 'idle') {
        const snapResult = resolveDrawSnap(pt, { altKey: e.evt.altKey });
        const snapped = snapResult.point;
        applySnapGuidesFromResult(snapResult);
        const preview = current.draftPreviewPoint;
        if (
          !preview ||
          Math.hypot(snapped[0] - preview[0], snapped[1] - preview[1]) > 0.5
        ) {
          const next = { ...current, draftPreviewPoint: snapped };
          stateRef.current = next;
          applyState(next);
        }
      } else       if (activeTool === 'room' && roomDrawAnchor && interactionRef.current.mode === 'idle') {
        const snapResult = resolveDrawSnap(pt, { altKey: e.evt.altKey });
        const corner = resolveRectangularRoomCorner(roomDrawAnchor, snapResult.point);
        setRoomDrawPreview(corner);
        applySnapGuidesFromResult(snapResult);
      } else if (activeTool !== 'wall' && interactionRef.current.mode !== 'draggingPoint') {
        setSnapGuides([]);
      }

      if (activeTool === 'door' || activeTool === 'window') {
        setOpeningPointerPoint(pt);
        const nearest = findNearestWall(current, pt, snapTolerancePx / displayScale + 16);
        setOpeningPreview(nearest);
      } else {
        setOpeningPreview(null);
        setOpeningPointerPoint(null);
      }

      if (interactionRef.current.mode === 'idle') {
        const hoverWall = findNearestWall(current, pt, snapTolerancePx / displayScale + 10);
        const hitRadius = visualHandleRadius(displayScale) + 4 / displayScale;
        let bestPointId = null;
        let bestDist = Infinity;
        for (const p of current.points) {
          const dist = Math.hypot(p.x - pt[0], p.y - pt[1]);
          if (dist < bestDist) {
            bestDist = dist;
            bestPointId = p.id;
          }
        }
        const pointHoverId = bestPointId && bestDist <= hitRadius ? bestPointId : null;
        if (hoverTarget?.kind !== 'opening') {
          if (pointHoverId) {
            setHover('point', pointHoverId);
          } else if (hoverWall?.wallId) {
            setHover('wall', hoverWall.wallId);
          } else {
            clearHover();
          }
        }
      }

      if (
        panDragRef.current.active &&
        interactionRef.current.mode === 'panning' &&
        activeTool === 'select' &&
        !wallDragPendingRef.current
      ) {
        const pointer = stage.getPointerPosition();
        if (!pointer) return;
        setPanX(panDragRef.current.originPanX + (pointer.x - panDragRef.current.startX));
        setPanY(panDragRef.current.originPanY + (pointer.y - panDragRef.current.startY));
      }
    },
    [
      pointerToImage,
      activeTool,
      roomDrawAnchor,
      resolveDrawSnap,
      applyState,
      snapTolerancePx,
      displayScale,
      applyWallDragAtPointer,
      startWallDrag,
      applySnapGuidesFromResult,
      hoverTarget,
      setHover,
      clearHover,
      isHovered,
    ],
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (activeTool === 'room' && e.evt.button === 0 && interactionRef.current.mode === 'idle') {
        const stage = e.target.getStage();
        if (e.target !== stage) return;
        const pt = pointerToImage(stage);
        if (!pt) return;
        const snapResult = resolveDrawSnap(pt, { altKey: e.evt.altKey });
        setRoomDrawAnchor(snapResult.point);
        setRoomDrawPreview(snapResult.point);
        return;
      }

      if (activeTool !== 'select' || e.evt.button !== 0) return;
      if (wallDragPendingRef.current) return;
      if (interactionRef.current.mode !== 'idle') return;
      const stage = e.target.getStage();
      if (e.target !== stage) return;
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      panDragRef.current = {
        active: true,
        startX: pointer.x,
        startY: pointer.y,
        originPanX: panX,
        originPanY: panY,
      };
      interactionRef.current = { mode: 'panning' };
    },
    [activeTool, panX, panY, pointerToImage, resolveDrawSnap],
  );

  const finalizeDragState = useCallback(
    (nextState) => {
      const cleaned = cleanupFloorPlanState(nextState);
      const synced = syncRoomsMeta(cleaned, findClosedPolygons(cleaned));
      stateRef.current = synced;
      if (historyApi) {
        historyApi.endTransaction(synced);
      } else {
        onStateChange(synced);
      }
      syncSelectionAfterClean(synced);
    },
    [historyApi, onStateChange, syncSelectionAfterClean],
  );

  const handleMouseUp = useCallback(() => {
    if (activeTool === 'room' && roomDrawAnchor && roomDrawPreview) {
      if (isRectangularRoomLargeEnough(stateRef.current, roomDrawAnchor, roomDrawPreview)) {
        const next = addRectangularRoom(stateRef.current, roomDrawAnchor, roomDrawPreview);
        pushHistory(next);
        onActiveToolChange('select');
      }
      setRoomDrawAnchor(null);
      setRoomDrawPreview(null);
    }

    if (interactionRef.current.mode === 'draggingWall') {
      designerDebug('wall drag end', interactionRef.current.wallId);
      finalizeDragState(stateRef.current);
      markDragCompleted();
    }
    wallDragPendingRef.current = null;
    panDragRef.current.active = false;
    interactionRef.current = { mode: 'idle' };
    setLiveDimWallIds([]);
    setSnapGuides([]);
  }, [activeTool, roomDrawAnchor, roomDrawPreview, pushHistory, finalizeDragState, onActiveToolChange, markDragCompleted]);

  const preventCanvasContextMenu = useCallback((e) => {
    e.preventDefault();
  }, []);

  const visibleBounds = useMemo(
    () => getVisibleBounds(stageWidth, stageHeight, viewportTransform, 3, ppm),
    [stageWidth, stageHeight, viewportTransform, ppm],
  );

  const handleRadius = visualHandleRadius(displayScale);
  const previewWallStrokeWidth = DEFAULT_WALL_THICKNESS_M * ppm;

  const draftStart = useMemo(() => {
    const chain = state.activeChainPointIds;
    if (!chain.length) return null;
    const pt = getPointById(state, chain[chain.length - 1]);
    return pt ? [pt.x, pt.y] : null;
  }, [state]);

  const draftWallDimension = useMemo(() => {
    if (!draftStart || !state.draftPreviewPoint) return null;
    return buildCenterlineWallDimension(draftStart, state.draftPreviewPoint, mpp, {
      roomPolygons,
      displayScale,
    });
  }, [draftStart, displayScale, mpp, roomPolygons, state.draftPreviewPoint]);

  const dimWallIdKey = useMemo(
    () =>
      [
        ...(hoverWallId ? [hoverWallId] : []),
        ...(selection?.type === 'wall' ? [selection.id] : []),
        ...liveDimWallIds,
      ]
        .sort()
        .join('|'),
    [hoverWallId, liveDimWallIds, selection],
  );

  const dimWallIds = useMemo(() => new Set(dimWallIdKey.split('|').filter(Boolean)), [dimWallIdKey]);

  const selectedWall =
    selection?.type === 'wall'
      ? getWallSegmentsResolved(state).find((s) => s.id === selection.id) ?? null
      : null;
  const selectedWallRecord =
    selection?.type === 'wall'
      ? state.walls.find((wall) => wall.id === selection.id) ?? null
      : null;
  const selectedPoint =
    selection?.type === 'point'
      ? getPointById(state, selection.id) ?? null
      : null;
  const selectedOpening =
    selection?.type === 'opening'
      ? getOpeningById(state, selection.id) ?? null
      : null;

  const openingItems = useMemo(
    () => buildWallMountedOpeningItems(state, segments, mpp),
    [state, segments, mpp],
  );

  const selectedOpeningRender = selectedOpening
    ? openingItems.find((item) => item.id === selectedOpening.id) ?? null
    : null;

  const openingPlacementDot = useMemo(() => {
    if (activeTool !== 'door' && activeTool !== 'window') return null;
    if (openingPreview?.projected) return openingPreview.projected;
    return openingPointerPoint;
  }, [activeTool, openingPreview, openingPointerPoint]);

  const openingPreviewRect = useMemo(() => {
    if (!openingPreview || (activeTool !== 'door' && activeTool !== 'window')) return null;
    const widthM =
      openingPreset?.widthMeters ??
      (activeTool === 'door' ? DEFAULT_DOOR.widthMeters : DEFAULT_WINDOW.widthMeters);
    const halfPx = (widthM / mpp) / 2;
    const thicknessPx = OPENING_2D_THICKNESS_M / mpp;
    const layout = openingPreviewLayout(
      { start: openingPreview.start, end: openingPreview.end },
      openingPreview.centerRatio,
      halfPx,
      thicknessPx,
    );
    const angle = Math.atan2(
      openingPreview.end[1] - openingPreview.start[1],
      openingPreview.end[0] - openingPreview.start[0],
    );
    if (!Number.isFinite(angle)) return null;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const centerX =
      openingPreview.start[0] +
      (openingPreview.end[0] - openingPreview.start[0]) * openingPreview.centerRatio;
    const centerY =
      openingPreview.start[1] +
      (openingPreview.end[1] - openingPreview.start[1]) * openingPreview.centerRatio;
    const p0 = [centerX - halfPx * cos, centerY - halfPx * sin];
    const p1 = [centerX + halfPx * cos, centerY + halfPx * sin];
    const maxPreviewWidthPx = Math.max(canvasW, canvasH) * 0.5;
    return { layout, angle, p0, p1, valid: true, maxPreviewWidthPx };
  }, [openingPreview, activeTool, openingPreset, mpp, canvasW, canvasH]);

  const handleOpeningSelect = useCallback(
    (openingId, e) => {
      if (shouldIgnoreSelectionClickAfterDrag()) return;
      selectOpening(openingId, captureOverlayAnchorFromEvent(e));
    },
    [selectOpening, captureOverlayAnchorFromEvent, shouldIgnoreSelectionClickAfterDrag],
  );

  const handleOpeningDragStart = useCallback(
    (openingId, e) => {
      if (interactionRef.current.mode !== 'idle') return;
      historyApi?.beginTransaction();
      const opening = getOpeningById(stateRef.current, openingId);
      interactionRef.current = {
        mode: 'draggingOpening',
        openingId,
        origin: opening
          ? { wallId: opening.wallId, centerRatio: opening.centerRatio }
          : null,
        lastValid: null,
      };
      selectOpening(openingId, undefined, { suppressMenu: true });
    },
    [selectOpening, historyApi],
  );

  const handleOpeningDragMove = useCallback(
    (openingId, e) => {
      if (
        interactionRef.current.mode !== 'draggingOpening' ||
        interactionRef.current.openingId !== openingId
      ) {
        return;
      }
      const stage = e.target.getStage();
      const pointer = stage ? pointerToImage(stage) : null;
      if (!pointer) return;
      const opening = getOpeningById(stateRef.current, openingId);
      if (!opening) return;
      const widthM =
        Number(opening.widthMeters) ||
        (opening.type === 'door' ? DEFAULT_DOOR.widthMeters : DEFAULT_WINDOW.widthMeters);
      const placement = resolveOpeningWallPlacement(
        stateRef.current,
        pointer,
        { ...opening, widthMeters: widthM },
        {
          snapThresholdPx: snapTolerancePx / displayScale + 16,
          preferCurrentWallThresholdPx: snapTolerancePx / displayScale + 8,
          metersPerPixel: mpp,
        },
      );
      if (!placement) return;
      interactionRef.current.lastValid = {
        wallId: placement.wallId,
        centerRatio: placement.centerRatio,
      };
      const nextCenter = pointAtSegmentRatio(
        placement.seg.start,
        placement.seg.end,
        placement.centerRatio,
      );
      e.target.position({ x: nextCenter[0], y: nextCenter[1] });
      const next = updateOpening(stateRef.current, openingId, {
        wallId: placement.wallId,
        centerRatio: placement.centerRatio,
      });
      stateRef.current = next;
      applyState(next);
    },
    [mpp, applyState, pointerToImage, snapTolerancePx, displayScale],
  );

  const handleOpeningDragEnd = useCallback(() => {
    if (interactionRef.current.mode !== 'draggingOpening') return;
    const { openingId, origin, lastValid } = interactionRef.current;
    let next = stateRef.current;
    if (!lastValid && origin) {
      next = updateOpening(stateRef.current, openingId, {
        wallId: origin.wallId,
        centerRatio: origin.centerRatio,
      });
    }
    finalizeDragState(next);
    markDragCompleted();
    interactionRef.current = { mode: 'idle' };
  }, [finalizeDragState, markDragCompleted]);

  const selectedRoomDetails = useMemo(() => {
    if (!selectedRoom) return null;
    const room =
      detectedRooms.find((r) => r.id === selectedRoom.roomId) ??
      closedRooms.find((r) => getRoomIdForDetectedRoom(r) === selectedRoom.roomId) ??
      closedRooms[selectedRoom.roomIndex] ??
      null;
    if (!room) return null;
    const roomId = getRoomIdForDetectedRoom(room);
    const meta = getRoomMeta(state, roomId);
    const { widthM, depthM } = roomAxisAlignedBoundsM(room.polygon, mpp);
    return {
      roomId,
      room,
      name: meta?.name ?? `Room-${selectedRoom.roomIndex + 1}`,
      widthM,
      depthM,
      visibleIn3D: meta?.visibleIn3D ?? true,
    };
  }, [selectedRoom, detectedRooms, closedRooms, state, mpp]);

  const roomActionsAnchor = useMemo(() => {
    if (!selectionMenuVisible || selectionMenuSuppressed) return null;
    if (!selectedRoom || !floatingActionsAnchor) return null;
    return floatingActionsAnchor;
  }, [selectedRoom, floatingActionsAnchor, selectionMenuVisible, selectionMenuSuppressed]);

  useEffect(() => {
    if (!selectedRoomDetails || !roomActionsAnchor) {
      onRoomSelectionChange?.(null);
      return;
    }
    onRoomSelectionChange?.({
      ...selectedRoomDetails,
      anchor: roomActionsAnchor,
    });
  }, [onRoomSelectionChange, roomActionsAnchor, selectedRoomDetails]);

  const wallActionsAnchor = useMemo(() => {
    if (!selectionMenuVisible || selectionMenuSuppressed) return null;
    if (selectedRoom || selectedOpening) return null;
    if (!selectedWall) return null;
    if (floatingActionsAnchor && selection?.type === 'wall') {
      return floatingActionsAnchor;
    }
    const mid = segmentMidpoint(selectedWall.start, selectedWall.end);
    return worldToOverlay(mid, viewportTransform);
  }, [selectedWall, selectedRoom, selectedOpening, selection, floatingActionsAnchor, viewportTransform, selectionMenuVisible, selectionMenuSuppressed]);

  const pointActionsAnchor = useMemo(() => {
    if (!selectionMenuVisible || selectionMenuSuppressed) return null;
    if (selectedRoom || selectedOpening || !selectedPoint) return null;
    if (floatingActionsAnchor && selection?.type === 'point') {
      return floatingActionsAnchor;
    }
    const visual = getNodeVisualCenter(state, selectedPoint.id, ppm);
    const overlay = worldToOverlay([visual.x, visual.y], viewportTransform);
    return { x: overlay.x, y: overlay.y };
  }, [ppm, selectedPoint, selectedRoom, selectedOpening, selection, floatingActionsAnchor, state, viewportTransform, selectionMenuVisible, selectionMenuSuppressed]);

  const openingActionsAnchor = useMemo(() => {
    if (!selectionMenuVisible || selectionMenuSuppressed) return null;
    if (selectedRoom || !selectedOpeningRender) return null;
    if (floatingActionsAnchor && selection?.type === 'opening') {
      return floatingActionsAnchor;
    }
    return worldToOverlay(selectedOpeningRender.mid, viewportTransform);
  }, [selectedOpeningRender, selectedRoom, selection, floatingActionsAnchor, viewportTransform, selectionMenuVisible, selectionMenuSuppressed]);

  const selectedWallLengthM = selectedWall
    ? Math.hypot(selectedWall.end[0] - selectedWall.start[0], selectedWall.end[1] - selectedWall.start[1]) *
      mpp
    : 0;

  const selectedWallAngleDeg = selectedWall
    ? segmentAngleDeg(selectedWall.start, selectedWall.end)
    : 0;

  const selectedOpeningAngleDeg = selectedOpeningRender
    ? (selectedOpeningRender.angle * 180) / Math.PI
    : 0;

  const selectedOpeningWallLengthM = selectedOpening
    ? (() => {
        const seg = getWallSegmentsResolved(state).find((s) => s.id === selectedOpening.wallId);
        if (!seg) return 0;
        return Math.hypot(seg.end[0] - seg.start[0], seg.end[1] - seg.start[1]) * mpp;
      })()
    : 0;

  const overlayViewportRect = useMemo(
    () => ({ width: stageWidth, height: stageHeight }),
    [stageWidth, stageHeight],
  );

  const overlayBlockedBounds = useMemo(
    () => getDefaultFloorPlanBlockedBounds(stageWidth, stageHeight, { subcategoryPanelOpen }),
    [stageWidth, stageHeight, subcategoryPanelOpen],
  );

  const dimensionObstacles = useMemo(() => {
    /** @type {import('../features/floorPlanEditor/FloorPlanWorkspace/useOverlayPlacement.js').RectBounds[]} */
    const obstacles = [...overlayBlockedBounds];
    (state.points || []).forEach((point) => {
      const radius = 6;
      obstacles.push({
        x: point.x * displayScale + offsetX + panX - radius,
        y: point.y * displayScale + offsetY + panY - radius,
        width: radius * 2,
        height: radius * 2,
      });
    });
    closedRooms.forEach((room, idx) => {
      const roomId = getRoomIdForDetectedRoom(room);
      const meta = getRoomMeta(state, roomId);
      const roomName = meta?.name ?? `Room-${idx + 1}`;
      const { widthM, depthM } = roomAxisAlignedBoundsM(room.polygon, mpp);
      const labelText = formatRoomLabel(roomName, room.areaSqM, widthM, depthM);
      const [cx, cy] = polygonCentroid(room.polygon);
      const labelFontSize = 13;
      const longestLine = labelText.split('\n').reduce(
        (longest, line) => (line.length > longest.length ? line : longest),
        '',
      );
      const halfW = estimateTextHalfWidth(longestLine, labelFontSize);
      const halfH = labelFontSize * 1.35 * 1.1;
      obstacles.push({
        x: cx * displayScale + offsetX + panX - halfW,
        y: cy * displayScale + offsetY + panY - halfH,
        width: halfW * 2,
        height: halfH * 2,
      });
    });
    return obstacles;
  }, [
    closedRooms,
    displayScale,
    mpp,
    offsetX,
    offsetY,
    overlayBlockedBounds,
    panX,
    panY,
    state,
  ]);

  const viewportTransformForOverlays = useMemo(
    () => ({
      offsetX: offsetX + panX,
      offsetY: offsetY + panY,
      scale: displayScale,
    }),
    [displayScale, offsetX, offsetY, panX, panY],
  );

  const handleActionMenuBoundsChange = useCallback((bounds) => {
    setActionMenuBounds((prev) => {
      if (
        prev &&
        prev.placement === bounds.placement &&
        Math.abs(prev.x - bounds.x) < 0.5 &&
        Math.abs(prev.y - bounds.y) < 0.5 &&
        Math.abs(prev.width - bounds.width) < 0.5 &&
        Math.abs(prev.height - bounds.height) < 0.5
      ) {
        return prev;
      }
      return bounds;
    });
  }, []);

  const resolvedWallDimensions = useMemo(() => {
    const resolved = [];
    const placedTextBounds = [];

    segments
      .filter((seg) => dimWallIds.has(seg.id))
      .forEach((seg) => {
        const poly = getPhysicalWallPolygon(wallPolygons.get(seg.id));
        const edgeDims = poly
          ? buildWallEdgeDimensions(poly, mpp, { roomPolygons, displayScale })
          : [];
        const dims =
          edgeDims.length > 0
            ? edgeDims
            : (() => {
                const centerline = buildCenterlineWallDimension(seg.start, seg.end, mpp, {
                  roomPolygons,
                  displayScale,
                });
                return centerline ? [centerline] : [];
              })();

        dims.forEach((dim) => {
          const obstacles = [...dimensionObstacles, ...placedTextBounds];
          const isSelectedWall = selection?.type === 'wall' && selection.id === seg.id;
          const placement = resolveDimensionPlacement({
            start: dim.start,
            end: dim.end,
            sideSign: dim.sideSign,
            placementKind: dim.placementKind ?? 'exterior',
            lengthPx: dim.lengthPx,
            metersPerPixel: mpp,
            displayScale,
            text: dim.text,
            obstacles,
            actionMenuBounds: isSelectedWall ? actionMenuBounds : null,
            viewportTransform: viewportTransformForOverlays,
          });
          placedTextBounds.push(
            inflateDimensionTextBounds(placement.annotation, viewportTransformForOverlays),
          );
          resolved.push({
            wallId: seg.id,
            type: dim.type,
            start: dim.start,
            end: dim.end,
            lengthPx: dim.lengthPx,
            text: dim.text,
            sideSign: placement.sideSign,
            annotation: placement.annotation,
          });
        });
      });

    return resolved;
  }, [
    actionMenuBounds,
    dimWallIdKey,
    dimensionObstacles,
    displayScale,
    mpp,
    roomPolygons,
    segments,
    selection,
    viewportTransformForOverlays,
    wallPolygons,
  ]);

  useEffect(() => {
    if (!selection || selection.type !== 'opening') {
      setOpeningEditOpen(false);
    }
  }, [selection]);

  const snapGuideLines = useMemo(() => (
    snapGuides.map((guide, index) => {
      if (guide.type === 'angle') {
        const rad = (guide.angleDeg * Math.PI) / 180;
        const [ax, ay] = guide.anchor;
        return {
          key: `angle-${index}`,
          points: [
            ax - Math.cos(rad) * SNAP_GUIDE_RAY_LENGTH_PX,
            ay - Math.sin(rad) * SNAP_GUIDE_RAY_LENGTH_PX,
            ax + Math.cos(rad) * SNAP_GUIDE_RAY_LENGTH_PX,
            ay + Math.sin(rad) * SNAP_GUIDE_RAY_LENGTH_PX,
          ],
        };
      }
      if (guide.type === 'perpendicular') return null;
      return {
        key: `${guide.type}-${guide.sourcePointId || index}-${index}`,
        points: [guide.from.x, guide.from.y, guide.to.x, guide.to.y],
      };
    })
      .filter(Boolean)
  ), [snapGuides]);

  const perpendicularMarkers = useMemo(() => (
    snapGuides
      .map((guide, index) => {
        if (guide.type !== 'perpendicular') return null;
        const polygon = buildPerpendicularMarkerPolygon(guide, ppm);
        if (!polygon) return null;
        return {
          key: `perpendicular-${index}`,
          points: polygon.flatMap((point) => point),
        };
      })
      .filter(Boolean)
  ), [ppm, snapGuides]);

  const cursorClass =
    activeTool === 'wall' || activeTool === 'room'
      ? 'floor-plan-designer-canvas-wrap--pencil'
      : activeTool === 'door' || activeTool === 'window'
        ? 'floor-plan-designer-canvas-wrap--placement'
        : activeTool === 'select'
          ? 'floor-plan-designer-canvas-wrap--select'
          : '';

  return (
    <div
      ref={containerRef}
      className={`floor-plan-designer-canvas-wrap ${cursorClass}`.trim()}
      onContextMenu={preventCanvasContextMenu}
    >
      <Stage
        width={stageWidth}
        height={stageHeight}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMousemove={handleMouseMove}
        onMouseup={handleMouseUp}
        onClick={handleStageClick}
        onContextMenu={handleContextMenu}
      >
        <Layer>
          <Group x={offsetX + panX} y={offsetY + panY} scaleX={displayScale} scaleY={displayScale}>
            <Rect
              x={visibleBounds.minX}
              y={visibleBounds.minY}
              width={visibleBounds.maxX - visibleBounds.minX}
              height={visibleBounds.maxY - visibleBounds.minY}
              fill="#f8fafc"
              listening={false}
            />

            <MeterGridLines bounds={visibleBounds} pixelsPerMeter={ppm} stepM={snapGridMeters} stroke="#f1f5f9" />
            <MeterGridLines bounds={visibleBounds} pixelsPerMeter={ppm} stepM={0.5} stroke="#e2e8f0" />
            <MeterGridLines bounds={visibleBounds} pixelsPerMeter={ppm} stepM={1} stroke="#cbd5e1" strokeWidth={1.2} />

            {enableBackgroundReferenceImage && state.backgroundReferenceImage ? (
              <FloorPlanBackgroundImageLayer
                background={state.backgroundReferenceImage}
                metersPerPixel={mpp}
                displayScale={displayScale}
                editMode={backgroundEditMode}
                onTransform={(patch) => {
                  const next = updateBackgroundReferenceImage(stateRef.current, patch);
                  stateRef.current = next;
                  onStateChange(next);
                }}
              />
            ) : null}

            <Rect
              x={0}
              y={0}
              width={canvasW}
              height={canvasH}
              stroke={DRAWABLE_FRAME_COLOR}
              strokeWidth={screenConstants.frameStrokeWidth}
              listening={false}
            />

            {snapGuideLines.map((guide) => (
              <Line
                key={guide.key}
                points={guide.points}
                stroke="#22c55e"
                strokeWidth={screenConstants.snapGuideStrokeWidth}
                dash={screenConstants.snapGuideDash}
                listening={false}
                opacity={0.9}
              />
            ))}

            {perpendicularMarkers.map((marker) => (
              <Line
                key={marker.key}
                points={marker.points}
                closed
                fill={COLORS.perpendicularMarker}
                opacity={0.35}
                stroke={COLORS.perpendicularMarker}
                strokeWidth={screenConstants.snapGuideStrokeWidth}
                listening={false}
              />
            ))}

            {closedRooms.map((room, idx) => {
              const roomId = getRoomIdForDetectedRoom(room);
              const meta = getRoomMeta(state, roomId);
              const roomName = meta?.name ?? `Room-${idx + 1}`;
              const { widthM, depthM } = roomAxisAlignedBoundsM(room.polygon, mpp);
              const labelText = formatRoomLabel(roomName, room.areaSqM, widthM, depthM);
              const [cx, cy] = polygonCentroid(room.polygon);
              const labelFontSize = 13 / displayScale;
              const labelLineHeight = labelFontSize * 1.35;
              const isRoomSelected = selectedRoom?.roomId === roomId;
              const baseFillColor = meta?.floorColor ?? COLORS.floor;
              const fillColor = isRoomSelected ? COLORS.preview : baseFillColor;
              const longestLine = labelText.split('\n').reduce(
                (longest, line) => (line.length > longest.length ? line : longest),
                '',
              );
              return (
              <Group key={`room-${roomId}`}>
                <Line
                  points={room.polygon.flatMap((p) => p)}
                  closed
                  fill={fillColor}
                  stroke={isRoomSelected ? COLORS.wallSelected : fillColor}
                  strokeWidth={isRoomSelected ? 2 / displayScale : 1}
                  strokeScaleEnabled={false}
                  listening={false}
                  opacity={isRoomSelected ? 0.55 : 0.85}
                />
                <Text
                  x={cx}
                  y={cy}
                  text={labelText}
                  fontSize={labelFontSize}
                  fill="#57534e"
                  align="center"
                  offsetX={estimateTextHalfWidth(longestLine, labelFontSize)}
                  offsetY={labelLineHeight * 1.1}
                  listening={false}
                />
              </Group>
              );
            })}

            {segments.map((seg) => {
              const isHover = isHovered('wall', seg.id);
              const isSelected = selection?.type === 'wall' && selection.id === seg.id;
              const normalColor = seg.color || DEFAULT_WALL_COLOR || COLORS.wall;
              const color = resolveSelectableColor({
                isSelected,
                isHovered: isHover,
                normalColor,
              });
              const drawPolys = getWallPolygonDrawListForWall(seg.id, wallRenderModel);

              return (
                <Group key={seg.id}>
                  {drawPolys.map((poly, polyIndex) => (
                    <Line
                      key={`${seg.id}-poly-${polyIndex}`}
                      points={poly.flatMap((p) => p)}
                      closed
                      fill={color}
                      strokeEnabled={false}
                      listening={false}
                    />
                  ))}
                </Group>
              );
            })}

            {selectedRoom
              ? (() => {
                  const highlightedPhysical = new Set();
                  return selectedRoom.boundaryWallIds.map((boundaryWallId) => {
                    const bw = roomBoundaryModel.boundaryWallById.get(boundaryWallId);
                    if (!bw || highlightedPhysical.has(bw.physicalWallId)) return null;
                    highlightedPhysical.add(bw.physicalWallId);
                    const poly = getBoundaryWallVisualGeometry(bw, wallPolygons);
                    if (!poly) return null;
                    return (
                      <Line
                        key={`room-boundary-${boundaryWallId}`}
                        points={poly.flatMap((p) => p)}
                        closed
                        fill={COLORS.wallHover}
                        opacity={0.55}
                        strokeEnabled={false}
                        listening={false}
                      />
                    );
                  });
                })()
              : null}

            {SHOW_ROOM_BOUNDARY_DEBUG && selectedRoom
              ? selectedRoom.boundaryWallIds.map((boundaryWallId) => {
                  const bw = roomBoundaryModel.boundaryWallById.get(boundaryWallId);
                  if (!bw) return null;
                  const startPt = getPointById(state, bw.startPointId);
                  const endPt = getPointById(state, bw.endPointId);
                  if (!startPt || !endPt) return null;
                  const mid = segmentMidpoint([startPt.x, startPt.y], [endPt.x, endPt.y]);
                  const shortId =
                    boundaryWallId.length > 28
                      ? `${boundaryWallId.slice(0, 14)}…${boundaryWallId.slice(-10)}`
                      : boundaryWallId;
                  return (
                    <Text
                      key={`rbw-debug-${boundaryWallId}`}
                      x={mid[0]}
                      y={mid[1] - 6}
                      text={shortId}
                      fontSize={9}
                      fill="#94a3b8"
                      listening={false}
                    />
                  );
                })
              : null}

            {roomDrawAnchor && roomDrawPreview ? (
              <RectangularRoomPreview
                anchor={roomDrawAnchor}
                preview={roomDrawPreview}
                metersPerPixel={mpp}
                displayScale={displayScale}
                stroke={COLORS.preview}
                strokeWidth={previewWallStrokeWidth}
                fill={COLORS.preview}
              />
            ) : null}

            {draftStart && state.draftPreviewPoint && draftWallDimension ? (
              <>
                <Line
                  points={[...draftStart, ...state.draftPreviewPoint]}
                  stroke={COLORS.preview}
                  strokeWidth={previewWallStrokeWidth}
                  opacity={0.55}
                  listening={false}
                />
                <FloorPlanDesignerDimension
                  start={draftWallDimension.start}
                  end={draftWallDimension.end}
                  lengthPx={draftWallDimension.lengthPx}
                  metersPerPixel={mpp}
                  displayScale={displayScale}
                  sideSign={draftWallDimension.sideSign}
                  text={draftWallDimension.text}
                  fontSize={12}
                />
              </>
            ) : null}

            {resolvedWallDimensions.map((dim) => (
              <FloorPlanDesignerDimension
                key={`${dim.wallId}-${dim.type}`}
                start={dim.start}
                end={dim.end}
                lengthPx={dim.lengthPx}
                metersPerPixel={mpp}
                displayScale={displayScale}
                sideSign={dim.sideSign}
                text={dim.text}
                annotation={dim.annotation}
              />
            ))}

            <FloorPlanOpeningLayer
              items={openingItems}
              mpp={mpp}
              displayScale={displayScale}
              activeTool={activeTool}
              selectedOpeningId={selection?.type === 'opening' ? selection.id : null}
              hoverOpeningId={hoverOpeningId}
              onSelectOpening={handleOpeningSelect}
              onDragStart={handleOpeningDragStart}
              onDragMove={handleOpeningDragMove}
              onDragEnd={handleOpeningDragEnd}
              onHoverOpening={(openingId) => {
                if (openingId) setHover('opening', openingId);
                else clearHover();
              }}
              renderMode="visible"
            />

            <FloorPlanOpeningLayer
              items={openingItems}
              mpp={mpp}
              displayScale={displayScale}
              activeTool={activeTool}
              selectedOpeningId={selection?.type === 'opening' ? selection.id : null}
              hoverOpeningId={hoverOpeningId}
              onSelectOpening={handleOpeningSelect}
              onDragStart={handleOpeningDragStart}
              onDragMove={handleOpeningDragMove}
              onDragEnd={handleOpeningDragEnd}
              onHoverOpening={(openingId) => {
                if (openingId) setHover('opening', openingId);
                else clearHover();
              }}
              renderMode="hit"
            />

            {activeTool === 'select'
              ? segments.map((seg) => {
                  const wallHitWidthPx = Math.max(MIN_WALL_HIT_WIDTH_PX, DESIGNER_FIXED_SCREEN_HIT_PX);
                  return (
                    <Line
                      key={`${seg.id}-hit`}
                      points={[seg.start[0], seg.start[1], seg.end[0], seg.end[1]]}
                      stroke="transparent"
                      strokeWidth={wallHitWidthPx}
                      hitStrokeWidth={wallHitWidthPx}
                      strokeScaleEnabled={false}
                      onClick={(e) => handleWallSelect(e, seg.id)}
                      onContextMenu={(e) => handleWallSelect(e, seg.id)}
                      onMouseDown={(e) => {
                        if (e.evt.button !== 0 || interactionRef.current.mode !== 'idle') return;
                        e.cancelBubble = true;
                        e.evt.preventDefault();
                        const stage = e.target.getStage();
                        const pointer = stage.getPointerPosition();
                        const world = pointerToImage(stage);
                        if (!pointer || !world) return;
                        wallDragPendingRef.current = {
                          wallId: seg.id,
                          startScreenX: pointer.x,
                          startScreenY: pointer.y,
                          startWorld: world,
                        };
                      }}
                    />
                  );
                })
              : null}

            {state.points.map((pt) => {
              const isSelected = selection?.type === 'point' && selection.id === pt.id;
              const isHoveredPoint = isHovered('point', pt.id);
              const pointFill = isSelected
                ? COLORS.pointSelected
                : isHoveredPoint
                  ? COLORS.pointHover
                  : COLORS.point;
              const pointStroke = isSelected || isHoveredPoint ? COLORS.pointSelected : COLORS.pointStroke;
              const pointOpacity = isSelected ? 1 : isHoveredPoint ? 0.92 : 0.7;
              const pointRadiusBoost = isHoveredPoint && !isSelected ? 1.08 : 1;
              const visual = getNodeVisualCenter(state, pt.id, ppm);
              return (
                <Circle
                  key={pt.id}
                  x={visual.x}
                  y={visual.y}
                  radius={Math.max(visual.radiusPx, handleRadius) * pointRadiusBoost}
                  fill={pointFill}
                  stroke={pointStroke}
                  strokeWidth={isSelected || isHoveredPoint ? 2 / displayScale : 1.5 / displayScale}
                  opacity={pointOpacity}
                  strokeScaleEnabled={false}
                  draggable={activeTool === 'select'}
                  onClick={(e) => {
                    if (activeTool !== 'select') return;
                    if (shouldIgnoreSelectionClickAfterDrag()) return;
                    e.cancelBubble = true;
                    selectPoint(pt.id, captureOverlayAnchorFromEvent(e));
                  }}
                  onDragStart={() => {
                    if (interactionRef.current.mode !== 'idle') return;
                    historyApi?.beginTransaction();
                    interactionRef.current = { mode: 'draggingPoint' };
                    selectPoint(pt.id, undefined, { suppressMenu: true });
                    setSnapGuides([]);
                    setLiveDimWallIds(
                      stateRef.current.walls
                        .filter((w) => w.startPointId === pt.id || w.endPointId === pt.id)
                        .map((w) => w.id),
                    );
                  }}
                  onDragMove={(e) => {
                    if (interactionRef.current.mode !== 'draggingPoint') return;
                    const node = e.target;
                    const stage = node.getStage();
                    const world =
                      (stage && pointerToImage(stage)) || [node.x(), node.y()];
                    const clamped = clampToCanvas(world, canvasW, canvasH);
                    const snapResult = snapDraggedCorner(clamped, pt.id, stateRef.current, {
                      snapToGrid,
                      gridMeters: snapGridMeters,
                      snapToPoints,
                      snapToWalls: true,
                      snapTolerancePx,
                      displayScale,
                      angleSnapEnabled: true,
                      angleSnapToleranceDeg: ANGLE_SNAP_TOLERANCE_DEG,
                      disableSnap: e.evt.altKey,
                    });
                    const finalPoint = clampToCanvas(snapResult.point, canvasW, canvasH);
                    applySnapGuidesFromResult(snapResult);
                    node.position({ x: finalPoint[0], y: finalPoint[1] });
                    const next = movePoint(stateRef.current, pt.id, finalPoint);
                    stateRef.current = next;
                    onStateChange(next);
                    setLiveDimWallIds(
                      next.walls
                        .filter((w) => w.startPointId === pt.id || w.endPointId === pt.id)
                        .map((w) => w.id),
                    );
                  }}
                  onDragEnd={() => {
                    interactionRef.current = { mode: 'idle' };
                    setLiveDimWallIds([]);
                    setSnapGuides([]);
                    const pointSplit = splitWallsAtPointIfOnSegment(stateRef.current, pt.id);
                    finalizeDragState(pointSplit.state);
                    markDragCompleted();
                  }}
                />
              );
            })}

            {openingPreviewRect ? (
              <FloorPlanOpeningPlacementPreview
                type={activeTool === 'door' ? 'door' : 'window'}
                layout={openingPreviewRect.layout}
                valid={openingPreviewRect.valid}
                displayScale={displayScale}
                maxPreviewWidthPx={openingPreviewRect.maxPreviewWidthPx}
                p0={openingPreviewRect.p0}
                p1={openingPreviewRect.p1}
                angle={openingPreviewRect.angle}
              />
            ) : null}

            {openingPlacementDot ? (
              <Circle
                x={openingPlacementDot[0]}
                y={openingPlacementDot[1]}
                radius={handleRadius + 1}
                fill={activeTool === 'door' ? OPENING_COLORS.door : OPENING_COLORS.window}
                stroke="#ffffff"
                strokeWidth={1}
                strokeScaleEnabled={false}
                listening={false}
              />
            ) : null}

            {state.draftPreviewPoint && activeTool === 'wall' ? (
              <Circle
                x={state.draftPreviewPoint[0]}
                y={state.draftPreviewPoint[1]}
                radius={handleRadius + 1}
                fill={COLORS.preview}
                stroke="#ffffff"
                strokeWidth={1}
                strokeScaleEnabled={false}
                listening={false}
              />
            ) : null}

            {(state.objects || [])
              .filter((object) => object.type === 'furniture')
              .map((object) => {
                const widthPx = object.width / mpp;
                const depthPx = object.depth / mpp;
                return (
                  <Group
                    key={object.id}
                    x={object.x / mpp}
                    y={object.z / mpp}
                    rotation={(object.rotationY * 180) / Math.PI}
                    listening={false}
                  >
                    <Rect
                      x={-widthPx / 2}
                      y={-depthPx / 2}
                      width={widthPx}
                      height={depthPx}
                      fill={COLORS.wall}
                      opacity={0.28}
                      stroke={COLORS.wallSelected}
                      strokeWidth={1 / displayScale}
                      strokeScaleEnabled={false}
                    />
                    <Text
                      text={object.category}
                      fontSize={12 / displayScale}
                      fill={COLORS.point}
                      align="center"
                      width={widthPx}
                      x={-widthPx / 2}
                      y={-6 / displayScale}
                      listening={false}
                    />
                  </Group>
                );
              })}
          </Group>
        </Layer>
      </Stage>

      {selectedWall && !selectedRoom && wallActionsAnchor ? (
        <FloorPlanWallActions
          key={selection.id}
          anchor={wallActionsAnchor}
          viewportRect={overlayViewportRect}
          onBoundsChange={handleActionMenuBoundsChange}
          onAddPoint={() => {
            if (interactionRef.current.mode !== 'idle') return;
            const wallId = selectedWall.id;
            const result = insertPointOnWall(stateRef.current, wallId, 0.5);
            designerDebug('add corner', { wallId, newWallIds: result.newWallIds });
            pushHistory(result.state);
            if (result.newWallIds.length) {
              selectWall(result.newWallIds[0]);
            }
          }}
          onEdit={() => {
            if (interactionRef.current.mode !== 'idle') return;
            setWallEditOpen(true);
          }}
          onDelete={() => {
            if (interactionRef.current.mode !== 'idle') return;
            pushHistory(removeWall(stateRef.current, selectedWall.id));
            clearSelection();
          }}
          onChangeColor={() => {
            if (interactionRef.current.mode !== 'idle') return;
            setMaterialPickerTarget({ kind: 'wall', id: selectedWall.id });
          }}
        />
      ) : null}

      {selectedPoint && !selectedRoom && pointActionsAnchor ? (
        <SelectionActionMenu
          anchor={pointActionsAnchor}
          canvasBounds={overlayViewportRect}
          blockedBounds={overlayBlockedBounds}
          ariaLabel="Node actions"
          onBoundsChange={handleActionMenuBoundsChange}
          actions={[
            {
              key: 'delete',
              title: 'Delete node',
              onClick: () => {
                if (interactionRef.current.mode !== 'idle') return;
                pushHistory(removePointAndHealWalls(stateRef.current, selectedPoint.id));
                clearSelection();
              },
              Icon: Trash2,
            },
            {
              key: 'wall',
              title: 'Start wall from node',
              onClick: () => {
                if (interactionRef.current.mode !== 'idle') return;
                const next = {
                  ...stateRef.current,
                  activeChainPointIds: [selectedPoint.id],
                  draftPreviewPoint: [selectedPoint.x, selectedPoint.y],
                };
                stateRef.current = next;
                onStateChange(next);
                clearSelection();
                setSnapGuides([]);
                onActiveToolChange('wall');
              },
              Icon: LineDotRightHorizontal,
            },
          ]}
        />
      ) : null}

      {selectedOpening && !selectedRoom && openingActionsAnchor ? (
        <FloorPlanOpeningActions
          opening={selectedOpening}
          anchor={openingActionsAnchor}
          viewportRect={overlayViewportRect}
          onBoundsChange={handleActionMenuBoundsChange}
          disabled={interactionRef.current.mode !== 'idle'}
          onDelete={() => {
            if (interactionRef.current.mode !== 'idle') return;
            pushHistory(removeOpening(stateRef.current, selectedOpening.id));
            clearSelection();
          }}
          onFlipSwing={
            selectedOpening.type === 'door'
              ? () => {
                  if (interactionRef.current.mode !== 'idle') return;
                  const current = getOpeningById(stateRef.current, selectedOpening.id);
                  if (!current) return;
                  pushHistory(
                    updateOpening(stateRef.current, selectedOpening.id, {
                      swingSide: current.swingSide === 'outside' ? 'inside' : 'outside',
                    }),
                  );
                }
              : undefined
          }
          onEdit={() => {
            if (interactionRef.current.mode !== 'idle') return;
            setOpeningEditOpen(true);
          }}
        />
      ) : null}

      {wallEditOpen && selectedWallRecord && selectedWall ? (
        <FloorPlanWallEditPopup
          wall={selectedWallRecord}
          wallLengthMeters={selectedWallLengthM}
          onClose={() => setWallEditOpen(false)}
          onEditMaterial={() => {
            setWallEditOpen(false);
            setMaterialPickerTarget({ kind: 'wall', id: selectedWall.id });
          }}
          onApply={(patch) => {
            if (interactionRef.current.mode !== 'idle') return false;
            const result = updateWall(stateRef.current, selectedWall.id, patch);
            if (!result.ok) return false;
            pushHistory(result.state);
            return true;
          }}
        />
      ) : null}

      {openingEditOpen && selectedOpening ? (
        <FloorPlanOpeningEditPopup
          opening={selectedOpening}
          wallLengthMeters={selectedOpeningWallLengthM}
          onApply={(patch) => {
            if (interactionRef.current.mode !== 'idle') return false;
            pushHistory(updateOpening(stateRef.current, selectedOpening.id, patch));
            return true;
          }}
          onClose={() => setOpeningEditOpen(false)}
        />
      ) : null}

      {selectedRoom && selectedRoomDetails && roomActionsAnchor ? (
        <FloorPlanRoomActions
          key={selectedRoomDetails.roomId}
          anchor={roomActionsAnchor}
          viewportRect={overlayViewportRect}
          onBoundsChange={handleActionMenuBoundsChange}
          onRenderRequest={
              onRenderRoomRequest
                ? () =>
                    onRenderRoomRequest({
                      ...selectedRoomDetails,
                      anchor: roomActionsAnchor,
                    })
                : undefined
            }
            onDelete={() => {
              if (interactionRef.current.mode !== 'idle') return;
              pushHistory(deleteRoom(stateRef.current, selectedRoom.wallIds));
              clearSelection();
            }}
            onChangeColor={() => {
              if (interactionRef.current.mode !== 'idle') return;
              setMaterialPickerTarget({ kind: 'room', id: selectedRoomDetails.roomId });
            }}
            onEdit={() => {
              if (interactionRef.current.mode !== 'idle') return;
            setRoomEditOpen(true);
          }}
        />
      ) : null}

      {roomEditOpen && selectedRoomDetails ? (
        <FloorPlanRoomEditPopup
          roomName={selectedRoomDetails.name}
          widthMeters={selectedRoomDetails.widthM}
          depthMeters={selectedRoomDetails.depthM}
          visibleIn3D={selectedRoomDetails.visibleIn3D}
          onApply={({ name, widthMeters, depthMeters, visibleIn3D }) => {
            if (interactionRef.current.mode !== 'idle') return false;
            let next = setRoomName(stateRef.current, selectedRoomDetails.roomId, name);
            const result = resizeRoomAxisAligned(
              next,
              selectedRoomDetails.room.pointIds,
              selectedRoomDetails.room.polygon,
              widthMeters,
              depthMeters,
            );
            if (!result.ok) return false;
            next = setRoomVisibleIn3D(result.state, selectedRoomDetails.roomId, visibleIn3D);
            pushHistory(next);
            return true;
          }}
          onClose={() => setRoomEditOpen(false)}
        />
      ) : null}

      {materialPickerTarget?.kind === 'wall' && selectedWall ? (
        <FloorPlanMaterialPickerPopup
          targetLabel="Wall"
          category="wall"
          initialMaterial={getWallMaterialConfig(
            stateRef.current.walls.find((w) => w.id === selectedWall.id),
          )}
          initialInsideMaterial={getWallMaterialConfig(
            stateRef.current.walls.find((w) => w.id === selectedWall.id),
            'inside',
          )}
          initialOutsideMaterial={getWallMaterialConfig(
            stateRef.current.walls.find((w) => w.id === selectedWall.id),
            'outside',
          )}
          onApply={(material, wallSide) => {
            pushHistory(
              setWallMaterial(stateRef.current, selectedWall.id, material, wallSide ?? 'inside'),
            );
          }}
          onClose={() => setMaterialPickerTarget(null)}
        />
      ) : null}

      {materialPickerTarget?.kind === 'room' && selectedRoomDetails ? (
        <FloorPlanMaterialPickerPopup
          targetLabel={`${selectedRoomDetails.name} floor`}
          category="floor"
          initialMaterial={getRoomFloorMaterialConfig(
            getRoomMeta(stateRef.current, selectedRoomDetails.roomId),
          )}
          onApply={(material) => {
            pushHistory(
              setRoomFloorMaterial(stateRef.current, selectedRoomDetails.roomId, material),
            );
          }}
          onClose={() => setMaterialPickerTarget(null)}
        />
      ) : null}
    </div>
  );
}

export default FloorPlanDesignerCanvas;
